import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { Alert, Box, Button, Skeleton, Typography } from "@mui/material";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import HeadsetMicRoundedIcon from "@mui/icons-material/HeadsetMicRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";

import LoggedInLayout from "../../layout/LoggedInLayout";
import { fetchSubscriptionPlans } from "../../services/api";
import PlanCard from "./PlanCard";

const TABS = [
  { key: "all", label: "All Plans" },
  { key: "counselling", label: "Counselling", categories: ["assessment", "counselling"] },
  { key: "admissions", label: "College Admissions", categories: ["admissions"] },
];

const HIGHLIGHTS = [
  { icon: VerifiedRoundedIcon, text: "Free assessment to start" },
  { icon: HeadsetMicRoundedIcon, text: "1:1 expert sessions" },
  { icon: PublicRoundedIcon, text: "India & abroad admissions" },
];

const FloatingBlob = ({ sx, duration, path }) => (
  <Box
    aria-hidden
    component={motion.div}
    animate={{ x: path.x, y: path.y }}
    transition={{ duration, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
    sx={{ position: "absolute", borderRadius: "50%", filter: "blur(40px)", ...sx }}
  />
);

FloatingBlob.propTypes = {
  sx: PropTypes.object.isRequired,
  duration: PropTypes.number.isRequired,
  path: PropTypes.shape({
    x: PropTypes.arrayOf(PropTypes.number).isRequired,
    y: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
};

const Hero = () => (
  <Box
    sx={{
      position: "relative",
      overflow: "hidden",
      borderRadius: { xs: "20px", md: "28px" },
      px: { xs: 2.5, sm: 4, md: 6 },
      py: { xs: 4, md: 6 },
      color: "#fff",
      background: "linear-gradient(135deg, #292E5C 0%, #3B3F7A 55%, #5B4B8A 100%)",
    }}
  >
    <FloatingBlob
      duration={9}
      path={{ x: [0, 40, -20], y: [0, 20, -10] }}
      sx={{ width: 260, height: 260, top: -90, right: -40, bgcolor: "rgba(246,170,72,0.55)" }}
    />
    <FloatingBlob
      duration={11}
      path={{ x: [0, -30, 20], y: [0, -20, 15] }}
      sx={{ width: 220, height: 220, bottom: -110, left: "30%", bgcolor: "rgba(236,72,153,0.35)" }}
    />
    <FloatingBlob
      duration={13}
      path={{ x: [0, 25, -15], y: [0, 30, -20] }}
      sx={{ width: 160, height: 160, top: 30, left: -60, bgcolor: "rgba(96,165,250,0.35)" }}
    />

    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      sx={{ position: "relative", maxWidth: 720 }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.8,
          px: 1.6,
          py: 0.6,
          borderRadius: "999px",
          bgcolor: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.22)",
          backdropFilter: "blur(6px)",
          fontSize: 13,
          fontWeight: 600,
          mb: 2,
        }}
      >
        <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: "#FCD34D" }} />
        Plans & Pricing
      </Box>
      <Typography
        component="h1"
        sx={{ fontSize: { xs: 30, sm: 38, md: 46 }, fontWeight: 800, lineHeight: 1.15 }}
      >
        Choose Your{" "}
        <Box
          component="span"
          sx={{
            background: "linear-gradient(90deg, #FCD34D, #F6AA48, #FB7185)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Path to Success
        </Box>
      </Typography>
      <Typography sx={{ fontSize: { xs: 15, md: 17 }, color: "rgba(255,255,255,0.82)", mt: 1.5, lineHeight: 1.6 }}>
        From a free career assessment to complete college admission support in India and abroad,
        pick the guidance that fits where you are today.
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1.2, sm: 2.5 }, mt: 3 }}>
        {HIGHLIGHTS.map(({ icon: Icon, text }, index) => (
          <Box
            key={text}
            component={motion.div}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + index * 0.1 }}
            sx={{ display: "flex", alignItems: "center", gap: 0.8, fontSize: 14, fontWeight: 500 }}
          >
            <Icon sx={{ fontSize: 20, color: "#F6AA48" }} />
            {text}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

const FilterTabs = ({ value, onChange, counts }) => (
  <Box
    role="tablist"
    aria-label="Filter plans"
    sx={{
      display: "inline-flex",
      flexWrap: "wrap",
      gap: 0.5,
      p: 0.6,
      borderRadius: "999px",
      bgcolor: "#fff",
      border: "1px solid #EEF0F4",
      boxShadow: "0 8px 24px -16px rgba(41,46,92,0.4)",
      maxWidth: "100%",
    }}
  >
    {TABS.map((tab) => {
      const selected = tab.key === value;
      return (
        <Box
          key={tab.key}
          component="button"
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => onChange(tab.key)}
          sx={{
            position: "relative",
            border: 0,
            background: "transparent",
            cursor: "pointer",
            px: { xs: 1.8, sm: 2.6 },
            py: 1.1,
            borderRadius: "999px",
            fontFamily: "inherit",
            fontSize: { xs: 13, sm: 14 },
            fontWeight: 600,
            color: selected ? "#fff" : "#4B5563",
            transition: "color 0.25s",
            "&:focus-visible": { outline: "2px solid #F6AA48", outlineOffset: 2 },
          }}
        >
          {selected && (
            <Box
              component={motion.span}
              layoutId="subscription-tab-pill"
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "999px",
                background: "linear-gradient(135deg, #F6AA48, #EA580C)",
                boxShadow: "0 8px 18px -8px rgba(234,88,12,0.7)",
              }}
            />
          )}
          <Box component="span" sx={{ position: "relative" }}>
            {tab.label}
            <Box component="span" sx={{ ml: 0.8, opacity: 0.75, fontWeight: 500 }}>
              {counts[tab.key] ?? 0}
            </Box>
          </Box>
        </Box>
      );
    })}
  </Box>
);

FilterTabs.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  counts: PropTypes.objectOf(PropTypes.number).isRequired,
};

const LoadingGrid = () => (
  <>
    {Array.from({ length: 6 }).map((_, index) => (
      <Box key={index} sx={{ bgcolor: "#fff", borderRadius: "24px", p: 3, border: "1px solid #EEF0F4" }}>
        <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: "16px" }} />
        <Skeleton width="40%" sx={{ mt: 2 }} />
        <Skeleton width="75%" height={32} />
        <Skeleton width="55%" height={52} sx={{ my: 1.5 }} />
        {[1, 2, 3].map((line) => <Skeleton key={line} width={`${90 - line * 10}%`} />)}
        <Skeleton variant="rounded" height={50} sx={{ mt: 3, borderRadius: "14px" }} />
      </Box>
    ))}
  </>
);

const Subscription = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("all");

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetchSubscriptionPlans();
      setPlans(response?.data?.data || []);
    } catch {
      setError("We couldn't load the plans right now. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const counts = useMemo(
    () =>
      Object.fromEntries(
        TABS.map((item) => [
          item.key,
          item.categories ? plans.filter((plan) => item.categories.includes(plan.category)).length : plans.length,
        ])
      ),
    [plans]
  );

  // Keep each plan's position in the full list so its colour doesn't change when filtering.
  const visiblePlans = useMemo(() => {
    const categories = TABS.find((item) => item.key === tab)?.categories;
    return plans
      .map((plan, index) => ({ plan, index }))
      .filter(({ plan }) => !categories || categories.includes(plan.category));
  }, [plans, tab]);

  const handleAction = (plan) => {
    const link = (plan.cta_link || "").trim();
    if (!link) return;
    if (link.startsWith("/")) {
      navigate(link);
    } else if (/^https?:\/\//.test(link)) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = link;
    }
  };

  return (
    <LoggedInLayout searchBar={false}>
      <MotionConfig reducedMotion="user">
        <Box sx={{ maxWidth: 1240, mx: "auto", px: { xs: 0, sm: 1 }, pt: { xs: 2.5, md: 4 }, pb: 6 }}>
          <Hero />

          <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 3, md: 4 }, mb: { xs: 3, md: 4 } }}>
            <FilterTabs value={tab} onChange={setTab} counts={counts} />
          </Box>

          {error && (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={loadPlans}>Retry</Button>}
              sx={{ mb: 3, borderRadius: "12px" }}
            >
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
              gap: { xs: 2.5, md: 3 },
              alignItems: "stretch",
            }}
          >
            {loading ? (
              <LoadingGrid />
            ) : (
              <AnimatePresence mode="popLayout">
                {visiblePlans.map(({ plan, index }) => (
                  <PlanCard key={plan.plan_id} plan={plan} index={index} onAction={handleAction} />
                ))}
              </AnimatePresence>
            )}
          </Box>

          {!loading && !error && visiblePlans.length === 0 && (
            <Typography sx={{ textAlign: "center", color: "#6B7280", py: 6 }}>
              New plans are coming soon. Check back shortly!
            </Typography>
          )}

          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            sx={{
              mt: { xs: 4, md: 6 },
              borderRadius: "24px",
              p: { xs: 3, md: 4 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2.5,
              background: "linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)",
              border: "1px solid #FED7AA",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  flexShrink: 0,
                  borderRadius: "16px",
                  display: "grid",
                  placeItems: "center",
                  color: "#fff",
                  background: "linear-gradient(135deg, #F6AA48, #EA580C)",
                }}
              >
                <SupportAgentRoundedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 700, color: "#171725" }}>
                  Not sure which plan is right for you?
                </Typography>
                <Typography sx={{ fontSize: 14, color: "#6B7280", mt: 0.3 }}>
                  Talk to one of our counsellors and we&apos;ll help you choose.
                </Typography>
              </Box>
            </Box>
            <Button
              onClick={() => navigate("/council")}
              sx={{
                flexShrink: 0,
                px: 3,
                height: 48,
                borderRadius: "14px",
                textTransform: "none",
                fontSize: 15,
                fontWeight: 700,
                color: "#fff",
                bgcolor: "#292E5C",
                "&:hover": { bgcolor: "#1E2247" },
              }}
            >
              Talk to a Counsellor
            </Button>
          </Box>
        </Box>
      </MotionConfig>
    </LoggedInLayout>
  );
};

export default Subscription;
